import { useCallback, useMemo } from 'react'
import { dateTime } from '@guestgoodie/helpers'

import Input from '@/components/ui/Input'
import { Trans } from '@lingui/react/macro'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'

interface DateTimeProps {
  name?: string
  invalid?: boolean
  value?: Date
  onChange: (value: Date) => void
  readOnly?: boolean

  // This can be used to show the DateTimeInput like +X Days, and the user can just select how much he wants to have Offset from the first day
  startDate?: Date
  isOffset?: boolean
}

export default function DateTimeInput({
  invalid = false,
  name,
  value,
  isOffset = false,
  readOnly = false,
  startDate,
  onChange
}: DateTimeProps) {
  const date = useMemo(() => value?.toISOString().split('T')[0], [value])
  const time = useMemo(() => dateTime.timeFormatter.format(value), [value])

  const onChangeDate = useCallback(
    (date: string) => {
      if (!date) return

      const newDate = new Date(`${date}T${time}Z`)

      onChange(newDate)
    },
    [time, onChange]
  )

  const onChangeTime = useCallback(
    (time: string) => {
      const newTime = time ? time : '00:00'

      const newDate = new Date(`${date}T${newTime}Z`)

      onChange(newDate)
    },
    [date, onChange]
  )

  const onChangeOffset = useCallback(
    (offset: string) => {
      const offsetNum = parseInt(offset)

      if (!startDate) return
      if (isNaN(offsetNum)) return

      const day = dateTime.toDay(dateTime.addDay(startDate, offsetNum))
      const date = day.toISOString().split('T')[0]
      const newDate = new Date(`${date}T${time}Z`)

      onChange(newDate)
    },
    [onChange, startDate, time]
  )

  const offset = useMemo(() => {
    if (!startDate) return
    if (!date) return 0

    return dateTime.dayDifference(startDate, new Date(date))
  }, [startDate, date])

  const showCounter = isOffset && offset != null

  return (
    <div className="flex flex-row items-center gap-2">
      {showCounter && (
        <div className="flex flex-row gap-1 items-center">
          <HugeiconsIcon icon={PlusSignIcon} className="size-4 text-text" />
          <div className="w-20">
            <Input
              type="number"
              value={offset}
              onChange={event => onChangeOffset(event.target.value)}
              name={`${name} day`}
              invalid={invalid}
              min={0}
              readOnly={readOnly}
            />
          </div>
          <Trans>Days</Trans>
        </div>
      )}
      {!showCounter && (
        <Input
          type="date"
          className="flex-1"
          value={date}
          onChange={event => onChangeDate(event.target.value)}
          name={`${name} day`}
          invalid={invalid}
          readOnly={readOnly}
        />
      )}
      <Input
        type="time"
        className="flex-1"
        value={time}
        onChange={event => onChangeTime(event.target.value)}
        name={`${name} time`}
        invalid={invalid}
        readOnly={readOnly}
      />
    </div>
  )
}
